import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  createFilterOptions,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import {
  buildActiveChips,
  clearFilterField,
  countActiveFilters,
  hasActiveFilters,
  resetFilters,
  statusFilterOptions,
} from "./filtering";
import {
  DEFAULT_SORT_RULES,
  FILTER_FIELD_LABELS,
  OVERDUE_FILTER_OPTIONS,
  PRACTICE_AREA_OPTIONS,
  SORT_DIRECTION_OPTIONS,
  SORT_FIELD_OPTIONS,
  STATUS_FILTER_NONE,
  type FilterFieldKey,
  type FilingFiltersState,
  type SortableColumn,
  type SortDirection,
  type SortRule,
} from "./types";

type FilterOptions = {
  caseName: string[];
  taskName: string[];
  practiceArea: string[];
};

type FilingReportFiltersProps = {
  filters: FilingFiltersState;
  filterOptions: FilterOptions;
  sortRules: SortRule[];
  loading: boolean;
  downloading: boolean;
  canDownload: boolean;
  onLoad: () => void;
  onApply: (next: FilingFiltersState, sortRules: SortRule[]) => void;
  onDownload: () => void;
  onClear: () => void;
};

const ALL_OPTION = "All";

const stringFilterOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (option) => option,
  trim: true,
  ignoreCase: true,
});

function FilterAutocomplete({
  field,
  value,
  options,
  getOptionLabel,
  onChange,
}: {
  field: FilterFieldKey;
  value: string;
  options: string[];
  getOptionLabel?: (option: string) => string;
  onChange: (value: string) => void;
}) {
  const optionsWithAll = useMemo(
    () => [ALL_OPTION, ...options.filter((o) => o !== ALL_OPTION)],
    [options]
  );

  return (
    <Autocomplete
      size="small"
      options={optionsWithAll}
      value={value || ALL_OPTION}
      onChange={(_, next) => onChange(!next || next === ALL_OPTION ? "" : next)}
      filterOptions={stringFilterOptions}
      getOptionLabel={(option) =>
        getOptionLabel && option !== ALL_OPTION ? getOptionLabel(option) : option
      }
      autoHighlight
      clearOnBlur={false}
      handleHomeEndKeys
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={FILTER_FIELD_LABELS[field]}
          placeholder="Type to filter…"
        />
      )}
      fullWidth
    />
  );
}

function SortRulesEditor({
  rules,
  onChange,
}: {
  rules: SortRule[];
  onChange: (rules: SortRule[]) => void;
}) {
  const usedColumns = useMemo(() => new Set(rules.map((rule) => rule.column)), [rules]);

  const updateRule = (index: number, patch: Partial<SortRule>) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const removeRule = (index: number) => {
    if (rules.length <= 1) return;
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRule = () => {
    const next = SORT_FIELD_OPTIONS.find((option) => !usedColumns.has(option.value));
    if (!next) return;
    onChange([...rules, { column: next.value, direction: "asc" }]);
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
        Sort by
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        First rule has highest priority
      </Typography>
      <Stack spacing={1}>
        {rules.map((rule, index) => (
          <Stack key={`${rule.column}-${index}`} direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 14 }}>
              {index + 1}.
            </Typography>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id={`filing-sort-col-${index}`}>Column</InputLabel>
              <Select
                labelId={`filing-sort-col-${index}`}
                label="Column"
                value={rule.column}
                onChange={(e) =>
                  updateRule(index, { column: e.target.value as SortableColumn })
                }
              >
                {SORT_FIELD_OPTIONS.filter(
                  (option) => option.value === rule.column || !usedColumns.has(option.value)
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 118 }}>
              <InputLabel id={`filing-sort-dir-${index}`}>Dir</InputLabel>
              <Select
                labelId={`filing-sort-dir-${index}`}
                label="Dir"
                value={rule.direction}
                onChange={(e) =>
                  updateRule(index, { direction: e.target.value as SortDirection })
                }
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              aria-label="Remove sort rule"
              onClick={() => removeRule(index)}
              disabled={rules.length <= 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addRule}
          disabled={usedColumns.size >= SORT_FIELD_OPTIONS.length}
          sx={{ alignSelf: "flex-start" }}
        >
          Add sort
        </Button>
      </Stack>
    </Box>
  );
}

function DateRangeFields({
  label,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  label: string;
  from: Dayjs | null;
  to: Dayjs | null;
  onFromChange: (value: Dayjs | null) => void;
  onToChange: (value: Dayjs | null) => void;
}) {
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1.25}>
        <DatePicker
          label="From"
          value={from}
          onChange={onFromChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={onToChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </Box>
  );
}

export function FilingReportFilters({
  filters,
  filterOptions,
  sortRules,
  loading,
  downloading,
  canDownload,
  onLoad,
  onApply,
  onDownload,
  onClear,
}: FilingReportFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<FilingFiltersState>(filters);
  const [draftSortRules, setDraftSortRules] = useState<SortRule[]>(sortRules);
  const filtersActive = hasActiveFilters(filters);
  const draftActive = hasActiveFilters(draft);
  const activeCount = countActiveFilters(filters);
  const activeChips = buildActiveChips(filters);

  useEffect(() => {
    if (drawerOpen) {
      setDraft(filters);
      setDraftSortRules(sortRules.length > 0 ? sortRules : [...DEFAULT_SORT_RULES]);
    }
  }, [drawerOpen, filters, sortRules]);

  const patchDraft = (patch: Partial<FilingFiltersState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const clearField = (key: keyof FilingFiltersState) => {
    onApply(clearFilterField(filters, key), sortRules);
  };

  const handleApply = () => {
    onApply(draft, draftSortRules.length > 0 ? draftSortRules : [...DEFAULT_SORT_RULES]);
    setDrawerOpen(false);
  };

  const clearDraft = () => setDraft(resetFilters());

  return (
    <>
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, flexShrink: 0, bgcolor: "grey.50" }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button variant="contained" size="small" onClick={onLoad} disabled={loading}>
            {loading ? <CircularProgress size={18} color="inherit" /> : "Load Report"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
            onClick={onDownload}
            disabled={downloading || loading || !canDownload}
          >
            Download
          </Button>
          <Badge
            badgeContent={activeCount}
            color="primary"
            overlap="rectangular"
            sx={{ "& .MuiBadge-badge": { right: 6, top: 6 } }}
          >
            <Button
              variant={filtersActive ? "contained" : "outlined"}
              color={filtersActive ? "primary" : "inherit"}
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setDrawerOpen(true)}
              sx={filtersActive ? undefined : { bgcolor: "background.paper" }}
            >
              Filters
            </Button>
          </Badge>
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<FilterAltOffIcon />}
            onClick={onClear}
            disabled={loading || !filtersActive}
          >
            Clear filters
          </Button>
        </Stack>

        {activeChips.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {activeChips.map((chip) => (
              <Chip
                key={String(chip.key)}
                size="small"
                label={chip.label}
                onDelete={() => clearField(chip.key)}
                sx={{ bgcolor: "background.paper" }}
              />
            ))}
          </Stack>
        )}
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                Filters & sort
              </Typography>
              {activeCount > 0 && (
                <Chip
                  size="small"
                  color="primary"
                  label={`${activeCount} active`}
                  sx={{ height: 22, fontWeight: 600, fontSize: "0.7rem" }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Apply to update the table
            </Typography>
          </Box>
          <IconButton aria-label="Close filters" onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
          <Stack spacing={1.75}>
            <FilterAutocomplete
              field="caseName"
              value={draft.caseName}
              options={filterOptions.caseName}
              onChange={(value) => patchDraft({ caseName: value })}
            />
            <FilterAutocomplete
              field="taskName"
              value={draft.taskName}
              options={filterOptions.taskName}
              onChange={(value) => patchDraft({ taskName: value })}
            />
            <FilterAutocomplete
              field="practiceArea"
              value={draft.practiceArea}
              options={[
                ...PRACTICE_AREA_OPTIONS.filter((o) => filterOptions.practiceArea.includes(o)),
                ...filterOptions.practiceArea.filter(
                  (o) => !(PRACTICE_AREA_OPTIONS as readonly string[]).includes(o)
                ),
              ]}
              onChange={(value) => patchDraft({ practiceArea: value })}
            />
            <FilterAutocomplete
              field="status"
              value={draft.status}
              options={statusFilterOptions()}
              getOptionLabel={(option) =>
                option === STATUS_FILTER_NONE ? "No status" : option
              }
              onChange={(value) => patchDraft({ status: value })}
            />
            <FilterAutocomplete
              field="overdue"
              value={draft.overdue}
              options={[...OVERDUE_FILTER_OPTIONS]}
              onChange={(value) => patchDraft({ overdue: value })}
            />
            <TextField
              size="small"
              label={FILTER_FIELD_LABELS.searchTerm}
              placeholder="Search…"
              value={draft.searchTerm}
              onChange={(e) => patchDraft({ searchTerm: e.target.value })}
              fullWidth
            />
            <DateRangeFields
              label="Created At"
              from={draft.createdFrom}
              to={draft.createdTo}
              onFromChange={(value) => patchDraft({ createdFrom: value })}
              onToChange={(value) => patchDraft({ createdTo: value })}
            />
            <DateRangeFields
              label="Due Date"
              from={draft.dueFrom}
              to={draft.dueTo}
              onFromChange={(value) => patchDraft({ dueFrom: value })}
              onToChange={(value) => patchDraft({ dueTo: value })}
            />
            <SortRulesEditor rules={draftSortRules} onChange={setDraftSortRules} />
          </Stack>
        </Box>

        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="warning"
              fullWidth
              startIcon={<FilterAltOffIcon />}
              onClick={clearDraft}
              disabled={!draftActive}
            >
              Clear filters
            </Button>
            <Button variant="contained" fullWidth onClick={handleApply} disabled={loading}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
